// CSS class name constants for status styling
export const STATUS_CLASS_NEGATIVE = 'status-negative';
export const STATUS_CLASS_POSITIVE = 'status-positive';
export const STATUS_CLASS_PENDING = 'status-pending';

// Display status constants
export const DISPLAY_STATUS_PENDING_APPROVAL = 'Pending Approval';
export const DISPLAY_STATUS_APPROVAL_DECLINED = 'Approval Declined';
export const DISPLAY_STATUS_APPROVED = 'Approved';
export const DISPLAY_STATUS_PENDING_PAYMENT = 'Pending Payment';
export const DISPLAY_STATUS_EMPTY = '';

// Statuses that should be displayed as "Pending Payment" for non-admin users
const PENDING_PAYMENT_STATUSES = [
  'Pending payment',
  'Initiate payment',
  'Payment initiated',
  'Funds available',
  'Scheduled',
  'Unscheduled',
  'Submit for approval',
  'submit_for_approval',
  'Submit to trust',
  'Submitted to trust',
  'Void'
];

// Statuses that should be displayed as "Pending Approval"
const PENDING_APPROVAL_STATUSES = [
  'Pending approval',
  'pending_approval'
];

// Statuses that should be displayed as "Approval Declined"
const APPROVAL_DECLINED_STATUSES = [
  'Approval declined',
  'declined'
];

// Statuses that should be displayed as "Approved"
const APPROVED_STATUSES = [
  'Approved',
  'approved'
];

// Statuses that should not be displayed to non-admin users
export const EXCLUDED_STATUSES = [
  'No statement available', 
  'No Statement Available', 
  'no_statement_available',
  'Skipped'
];

// Helper function to convert snake_case to Title Case
const convertSnakeCaseToTitleCase = (snakeStr: string): string => {
  return snakeStr
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Helper function to get the effective status (displayStatus or converted status)
const getEffectiveStatus = (displayStatus: string | null, status: string | null): string => {
  // Use displayStatus if it exists and is not empty
  if (displayStatus && displayStatus.trim()) {
    return displayStatus.trim();
  }
  
  // Fallback to status field, converting from snake_case if needed
  if (status && status.trim()) {
    const trimmedStatus = status.trim();
    // Check if it's in snake_case format
    if (trimmedStatus.includes('_')) {
      return convertSnakeCaseToTitleCase(trimmedStatus);
    }
    return trimmedStatus;
  }
  
  // Return empty string if both are empty
  return DISPLAY_STATUS_EMPTY;
};

// Map backend statuses to client-friendly statuses for non-admin users
export const mapStatusForClient = (displayStatus: string | null, status: string | null = null): string => {
  // Get the effective status to work with
  const effectiveStatus = getEffectiveStatus(displayStatus, status);
  
  // If no valid status, return empty string
  if (!effectiveStatus) {
    return DISPLAY_STATUS_EMPTY;
  }
  
  // Case-insensitive check for pending approval statuses
  if (PENDING_APPROVAL_STATUSES.some(pendingStatus => 
    effectiveStatus.toLowerCase() === pendingStatus.toLowerCase()
  )) {
    return DISPLAY_STATUS_PENDING_APPROVAL;
  }
  
  // Case-insensitive check for approval declined statuses
  if (APPROVAL_DECLINED_STATUSES.some(declinedStatus => 
    effectiveStatus.toLowerCase() === declinedStatus.toLowerCase()
  )) {
    return DISPLAY_STATUS_APPROVAL_DECLINED;
  }
  
  // Case-insensitive check for approved statuses
  if (APPROVED_STATUSES.some(approvedStatus => 
    effectiveStatus.toLowerCase() === approvedStatus.toLowerCase()
  )) {
    return DISPLAY_STATUS_APPROVED;
  }
  
  // Case-insensitive check for pending payment statuses
  if (PENDING_PAYMENT_STATUSES.some(pendingStatus => 
    effectiveStatus.toLowerCase() === pendingStatus.toLowerCase()
  )) {
    return DISPLAY_STATUS_PENDING_PAYMENT;
  }

  // For any other status, return as is
  return effectiveStatus;
};

// Simplified version for cases where only displayStatus is available
export const mapStatusForClientSimple = (status: string): string => {
  return mapStatusForClient(status, null);
};

// Get status CSS class based on status content
export const getStatusClass = (status: string): string => {
  const statusLower = status.toLowerCase();
  
  // Negative statuses - red
  const negativeWords = ['declined', 'rejected', 'failed', 'error', 'cancelled', 'void', 'denied'];
  if (negativeWords.some(word => statusLower.includes(word))) {
    return STATUS_CLASS_NEGATIVE;
  }
  
  // Positive statuses - green
  const positiveWords = ['approved', 'completed', 'paid', 'success', 'confirmed'];
  if (positiveWords.some(word => statusLower.includes(word))) {
    return STATUS_CLASS_POSITIVE;
  }
  
  // Default pending/neutral statuses - orange
  return STATUS_CLASS_PENDING;
};
